import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { isV2PatientVersion } from '@odo/shared-ui';
import { useReviewPatient } from '../../../../hooks/useReviewPatient';
import { useRecentActions } from '../../../../contexts/RecentActionsContext';
import { usePatientMetadata } from '../../../../providers/PatientMetadataProvider';
import { useToast } from '../../../../providers/ToastProvider';
import {
    approveRefinement,
    endTreatmentFromRefinement,
    refuseRefinementContinue
} from '../../../../services/patient/refinement';
import { PatientHeaderCard } from '../PatientApprove/PatientHeaderCard';
import { PatientActionPage } from '../shared/PatientActionPage';
import { ReviewMonitoringPane } from '../shared/ReviewMonitoringPane';
import { ReviewNotesPane } from '../shared/ReviewNotesPane';
import { RefinementFooter } from './RefinementFooter';
import { RejectRefinementModal } from './RejectRefinementModal';
import { EndTreatmentModal } from './EndTreatmentModal';

const CHECKLIST_URL =
    'https://drive.google.com/file/d/13d7nIAwIwvpewdiqoWruOmqVRY8rAbD2/view?usp=sharing';

type ModalKind = 'reject' | 'end' | null;

const PatientReviewRefinement: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: patient, isLoading } = useReviewPatient();
    const { getProfilePictureUrl } = usePatientMetadata();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const [modal, setModal] = useState<ModalKind>(null);
    const [submitting, setSubmitting] = useState(false);

    const patientId = patient?.id ?? null;
    const patientName = useMemo(() => patient?.name ?? '', [patient]);
    const profilePicUrl = patientId ? getProfilePictureUrl(patientId) : null;
    const dmId = patient?.dmId ?? null;
    const notesPaneProps = {
        patientId,
        triggerType: 'refinement_trigger',
        fallbackNotes: patient?.refinementNotes ?? null,
        checklistUrl: CHECKLIST_URL,
        checklistLabel: 'Refinement Scan Submission Checklist'
    } as const;

    const invalidate = (): void => {
        if (!patientId) return;
        queryClient.invalidateQueries({ queryKey: ['patients'] });
        queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
    };

    const handleProceed = async (): Promise<void> => {
        if (!patientId) return;
        setSubmitting(true);
        try {
            await approveRefinement(patientId);
            markActionCompleted(patientId, 'Review for refinement');
            showToast('Proceeding with refinement', 'success');
            invalidate();
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to approve refinement';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async (reason: string): Promise<void> => {
        if (!patientId) return;
        setSubmitting(true);
        try {
            await refuseRefinementContinue(patientId, reason);
            markActionCompleted(patientId, 'Review for refinement');
            showToast('Refinement rejected — treatment continues', 'success');
            invalidate();
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to reject refinement';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
            setModal(null);
        }
    };

    const handleEndTreatment = async (reason: string, retainerOption: string): Promise<void> => {
        if (!patientId) return;
        setSubmitting(true);
        try {
            await endTreatmentFromRefinement(patientId, retainerOption, reason);
            markActionCompleted(patientId, 'Review for refinement');
            showToast('Treatment ended', 'success');
            invalidate();
            navigate('/home');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to end treatment';
            showToast(message, 'error');
        } finally {
            setSubmitting(false);
            setModal(null);
        }
    };

    return (
        <PatientActionPage
            patientId={patientId}
            isLoading={isLoading}
            patientName={patientName}
            breadcrumbTitle="Review for refinement"
            tabIdPrefix="review-refinement"
            redirectToOverview={isV2PatientVersion(patient?.version)}
            leftPane={
                dmId !== null ? (
                    <ReviewMonitoringPane
                        patientId={patientId}
                        dmId={dmId}
                        totalAligners={patient?.totalAligners ?? null}
                    />
                ) : (
                    <ReviewNotesPane {...notesPaneProps} className="h-full" />
                )
            }
            caseContent={
                <>
                    <PatientHeaderCard
                        name={patientName || null}
                        email={patient?.email ?? null}
                        phone={patient?.phone ?? null}
                        dateOfBirth={patient?.dateOfBirth ?? null}
                        profilePicUrl={profilePicUrl}
                        caseType={patient?.caseType ?? null}
                        subCaseType={patient?.subCaseType ?? null}
                        manufacturer={patient?.manufacturer ?? null}
                        manufacturerLink={patient?.manufacturerLink ?? null}
                        duration={patient?.treatmentDuration ?? null}
                        compact
                    />
                    {dmId !== null && (
                        <ReviewNotesPane {...notesPaneProps} className="flex-1 min-h-32" />
                    )}
                </>
            }
            footer={
                <RefinementFooter
                    isSubmitting={submitting}
                    onProceed={() => void handleProceed()}
                    onReject={() => setModal('reject')}
                    onEndTreatment={() => setModal('end')}
                />
            }
        >
            <RejectRefinementModal
                open={modal === 'reject'}
                patientName={patientName}
                isSubmitting={submitting}
                onSubmit={(reason) => void handleReject(reason)}
                onClose={() => setModal(null)}
            />
            <EndTreatmentModal
                open={modal === 'end'}
                patientName={patientName}
                isSubmitting={submitting}
                onSubmit={(reason, retainer) => void handleEndTreatment(reason, retainer)}
                onClose={() => setModal(null)}
            />
        </PatientActionPage>
    );
};

export default PatientReviewRefinement;
